/**
 * AgencyPanel — Zarzadzanie kampaniami klientow (tryb agencyjny)
 * /admin/booster
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import PanelLayout from '@/components/PanelLayout';
import { Button } from '@/components/ui/button';
import { API } from '@/utils/api';
import {
  Megaphone, Plus, Play, Pause, CheckCircle2, Trash2, Copy,
  Loader2, ChevronRight, Users, BarChart3, Clock, Target,
  ExternalLink, Search, Filter, Eye, ArrowLeft, Send,
  ClipboardCopy, SkipForward, XCircle, Ban, AlertTriangle,
  Globe, Facebook, Image as ImageIcon, MessageSquare, Calendar
} from 'lucide-react';

const STATUS_MAP = {
  DRAFT: { label: 'Szkic', color: 'bg-zinc-100 text-zinc-600', dot: 'bg-zinc-400' },
  RUNNING: { label: 'Aktywna', color: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
  PAUSED: { label: 'Pauza', color: 'bg-yellow-100 text-yellow-700', dot: 'bg-yellow-500' },
  DONE: { label: 'Zakończona', color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
};

const POST_STATUS_MAP = {
  PENDING: { label: 'Oczekuje', color: 'text-zinc-500', bg: 'bg-zinc-50' },
  OPENED: { label: 'Otwarto', color: 'text-blue-600', bg: 'bg-blue-50' },
  COPIED: { label: 'Skopiowano', color: 'text-indigo-600', bg: 'bg-indigo-50' },
  POSTED: { label: 'Opublikowano', color: 'text-green-600', bg: 'bg-green-50' },
  SKIPPED: { label: 'Pominięto', color: 'text-zinc-400', bg: 'bg-zinc-50' },
  FAILED: { label: 'Blad', color: 'text-red-600', bg: 'bg-red-50' },
  REJECTED: { label: 'Odrzucono', color: 'text-orange-600', bg: 'bg-orange-50' },
  BANNED: { label: 'Ban', color: 'text-red-700', bg: 'bg-red-100' },
};

/* ───── StatusBadge ───── */
function StatusBadge({ status }) {
  const s = STATUS_MAP[status] || STATUS_MAP.DRAFT;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${s.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

/* ───── StatsBar ───── */
function StatsBar({ stats }) {
  if (!stats) return null;
  const total = (stats.posted || 0) + (stats.opened || 0) + (stats.copied || 0) + (stats.skipped || 0) + (stats.failed || 0) + (stats.rejected || 0) + (stats.banned || 0);
  return (
    <div className="flex items-center gap-3 text-xs">
      <span className="text-green-600 font-medium">{stats.posted || 0} opubl.</span>
      <span className="text-blue-600">{stats.opened || 0} otw.</span>
      <span className="text-zinc-400">{stats.skipped || 0} pom.</span>
      {(stats.failed || 0) > 0 && <span className="text-red-500">{stats.failed} err</span>}
      {total > 0 && <span className="text-zinc-300">| {total} total</span>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   CAMPAIGN LIST VIEW
   ═══════════════════════════════════════════════════════════════════ */
function CampaignListView({ campaigns, loading, onSelect, onNew, onRefresh, onAction, onGroups, globalStats }) {
  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');

  const filtered = campaigns.filter(c => {
    if (filter !== 'ALL' && c.status !== filter) return false;
    if (search && !c.name?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900" data-testid="agency-title">Agencja</h1>
          <p className="text-sm text-zinc-500 mt-1">Zarządzaj kampaniami klientów</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={onGroups} variant="outline" className="gap-2" data-testid="manage-groups-btn">
            <Users className="w-4 h-4" /> Grupy FB
          </Button>
          <Button onClick={onNew} className="bg-[#F47A3B] hover:bg-[#E06A2B] text-white gap-2" data-testid="new-campaign-btn">
            <Plus className="w-4 h-4" /> Nowa kampania
          </Button>
        </div>
      </div>

      {/* Stats Dashboard */}
      {globalStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="stats-dashboard">
          <div className="bg-white rounded-xl border border-zinc-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center">
                <Megaphone className="w-4 h-4 text-orange-500" />
              </div>
              <span className="text-xs text-zinc-500">Kampanie</span>
            </div>
            <p className="text-2xl font-bold text-zinc-900">{globalStats.total_campaigns}</p>
            <p className="text-xs text-green-600 mt-1">{globalStats.active_campaigns} aktywnych</p>
          </div>
          <div className="bg-white rounded-xl border border-zinc-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                <Users className="w-4 h-4 text-blue-500" />
              </div>
              <span className="text-xs text-zinc-500">Grupy FB</span>
            </div>
            <p className="text-2xl font-bold text-zinc-900">{globalStats.total_groups}</p>
          </div>
          <div className="bg-white rounded-xl border border-zinc-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
                <Send className="w-4 h-4 text-green-500" />
              </div>
              <span className="text-xs text-zinc-500">Opublikowane</span>
            </div>
            <p className="text-2xl font-bold text-zinc-900">{globalStats.posted_posts}</p>
            <p className="text-xs text-zinc-500 mt-1">z {globalStats.total_posts} total</p>
          </div>
          <div className="bg-white rounded-xl border border-zinc-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center">
                <BarChart3 className="w-4 h-4 text-purple-500" />
              </div>
              <span className="text-xs text-zinc-500">Skuteczność</span>
            </div>
            <p className="text-2xl font-bold text-zinc-900">{globalStats.post_rate}%</p>
            <p className="text-xs text-zinc-500 mt-1">publish rate</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <input
            type="text"
            placeholder="Szukaj kampanii..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#F47A3B]/30 focus:border-[#F47A3B]"
            data-testid="campaign-search"
          />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {['ALL', 'DRAFT', 'RUNNING', 'PAUSED', 'DONE'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                filter === f ? 'bg-[#F47A3B] text-white' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
              }`}
              data-testid={`filter-${f.toLowerCase()}`}
            >
              {f === 'ALL' ? 'Wszystkie' : STATUS_MAP[f]?.label || f}
            </button>
          ))}
        </div>
      </div>

      {/* Campaign Cards */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-[#F47A3B]" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-zinc-50 rounded-2xl border border-dashed border-zinc-200">
          <Megaphone className="w-10 h-10 mx-auto text-zinc-300 mb-3" />
          <p className="text-zinc-500 text-sm">Brak kampanii</p>
          <Button onClick={onNew} variant="outline" size="sm" className="mt-3 gap-1.5">
            <Plus className="w-3.5 h-3.5" /> Stwórz pierwszą
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(c => (
            <div
              key={c.id}
              className="bg-white rounded-xl border border-zinc-200 p-4 hover:shadow-md transition-shadow cursor-pointer group"
              onClick={() => onSelect(c.id)}
              data-testid={`campaign-card-${c.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-zinc-900 truncate">{c.name}</h3>
                    <StatusBadge status={c.status} />
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-zinc-500">
                    {c.landing_url && (
                      <span className="flex items-center gap-1"><Globe className="w-3 h-3" />{c.landing_url.replace(/https?:\/\//, '').slice(0, 40)}</span>
                    )}
                    {c.group_ids?.length > 0 && (
                      <span className="flex items-center gap-1"><Users className="w-3 h-3" />{c.group_ids.length} grup</span>
                    )}
                    {c.copy_variants?.length > 0 && (
                      <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" />{c.copy_variants.length} wariantów</span>
                    )}
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(c.created_at).toLocaleDateString('pl-PL')}</span>
                  </div>
                  <div className="mt-2">
                    <StatsBar stats={c.stats} />
                  </div>
                </div>
                <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                  {c.status === 'DRAFT' && (
                    <Button size="sm" variant="outline" className="gap-1 text-green-600 border-green-200 hover:bg-green-50" onClick={() => onAction(c.id, 'start')} data-testid={`start-${c.id}`}>
                      <Play className="w-3.5 h-3.5" /> Start
                    </Button>
                  )}
                  {c.status === 'RUNNING' && (
                    <Button size="sm" variant="outline" className="gap-1 text-yellow-600 border-yellow-200 hover:bg-yellow-50" onClick={() => onAction(c.id, 'pause')} data-testid={`pause-${c.id}`}>
                      <Pause className="w-3.5 h-3.5" /> Pauza
                    </Button>
                  )}
                  {c.status === 'PAUSED' && (
                    <Button size="sm" variant="outline" className="gap-1 text-green-600 border-green-200 hover:bg-green-50" onClick={() => onAction(c.id, 'start')} data-testid={`resume-${c.id}`}>
                      <Play className="w-3.5 h-3.5" /> Wznow
                    </Button>
                  )}
                  {['RUNNING', 'PAUSED'].includes(c.status) && (
                    <Button size="sm" variant="outline" className="gap-1 text-blue-600 border-blue-200 hover:bg-blue-50" onClick={() => onAction(c.id, 'finish')} data-testid={`finish-${c.id}`}>
                      <CheckCircle2 className="w-3.5 h-3.5" /> Zakoncz
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="text-zinc-400 hover:text-zinc-600" onClick={() => onAction(c.id, 'duplicate')} data-testid={`dup-${c.id}`}>
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                  <ChevronRight className="w-5 h-5 text-zinc-300 group-hover:text-[#F47A3B] transition-colors" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   QUEUE VIEW — tryb tasmy produkcyjnej
   ═══════════════════════════════════════════════════════════════════ */
function QueueView({ slots, campaign }) {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [completedIds, setCompletedIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`queue-done-${campaign.id}`) || '[]'); } catch { return []; }
  });
  const [phase, setPhase] = useState('ready'); // ready | publishing | dm_flow

  const pendingSlots = slots.filter((_, i) => !completedIds.includes(i));
  const current = pendingSlots.length > 0 ? slots[slots.indexOf(pendingSlots[0])] : null;
  const currentGlobalIdx = current ? slots.indexOf(current) : -1;
  const progress = slots.length > 0 ? ((completedIds.length / slots.length) * 100).toFixed(0) : 0;

  const CAT_COLORS = {
    'SPRZEDAZ': { bg: 'bg-green-500', text: 'text-green-700', badge: 'bg-green-100 text-green-700' },
    'ELEKTRONIKA': { bg: 'bg-blue-500', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-700' },
    'RANDKI': { bg: 'bg-pink-500', text: 'text-pink-700', badge: 'bg-pink-100 text-pink-700' },
    'MONETYZACJA': { bg: 'bg-yellow-500', text: 'text-yellow-700', badge: 'bg-yellow-100 text-yellow-700' },
  };

  const markDone = () => {
    const newCompleted = [...completedIds, currentGlobalIdx];
    setCompletedIds(newCompleted);
    localStorage.setItem(`queue-done-${campaign.id}`, JSON.stringify(newCompleted));
    setPhase('ready');
    toast.success(`Gotowe! Zostało ${pendingSlots.length - 1} grup`);
  };

  const skipCurrent = () => {
    const newCompleted = [...completedIds, currentGlobalIdx];
    setCompletedIds(newCompleted);
    localStorage.setItem(`queue-done-${campaign.id}`, JSON.stringify(newCompleted));
    setPhase('ready');
  };

  const resetQueue = () => {
    setCompletedIds([]);
    localStorage.removeItem(`queue-done-${campaign.id}`);
    setPhase('ready');
    toast.success('Kolejka zresetowana');
  };

  const handlePublish = () => {
    if (!current) return;
    navigator.clipboard.writeText(current.postText);
    window.open(current.group.group_url, '_blank');
    setPhase('publishing');
    toast.success('Tekst skopiowany! Grupa otwarta — wklej tekst, dodaj grafike, opublikuj');
  };

  if (!current) {
    return (
      <div className="text-center py-16 bg-green-50 rounded-2xl border border-green-200">
        <CheckCircle2 className="w-12 h-12 mx-auto text-green-500 mb-3" />
        <p className="text-green-700 font-bold text-lg">Wszystko opublikowane!</p>
        <p className="text-green-600 text-sm mt-1">{completedIds.length} postow ukonczone</p>
        <Button onClick={resetQueue} variant="outline" size="sm" className="mt-4 gap-1.5">
          <SkipForward className="w-3.5 h-3.5" /> Zacznij od nowa
        </Button>
      </div>
    );
  }

  const catStyle = CAT_COLORS[current.category] || CAT_COLORS.SPRZEDAZ;

  return (
    <div className="space-y-4">
      {/* Progress bar */}
      <div className="flex items-center gap-3">
        <div className="flex-1 bg-zinc-200 rounded-full h-2.5 overflow-hidden">
          <div className={`h-full rounded-full transition-all duration-500 ${catStyle.bg}`} style={{ width: `${progress}%` }} />
        </div>
        <span className="text-xs font-bold text-zinc-600">{completedIds.length}/{slots.length}</span>
        {completedIds.length > 0 && (
          <button onClick={resetQueue} className="text-[10px] text-zinc-400 hover:text-zinc-600">Reset</button>
        )}
      </div>

      {/* Current card */}
      <div className="bg-white rounded-2xl border-2 border-zinc-200 overflow-hidden shadow-lg">
        {/* Header */}
        <div className="px-5 py-3 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${catStyle.badge}`}>{current.categoryLabel}</span>
            <span className="text-xs text-zinc-500">{current.dayName} • {current.group.time} • {current.group.time_label}</span>
          </div>
          <button onClick={skipCurrent} className="text-xs text-zinc-400 hover:text-zinc-600 flex items-center gap-1">
            <SkipForward className="w-3 h-3" /> Pomiń
          </button>
        </div>

        {/* Group info */}
        <div className="px-5 py-3 bg-blue-50 border-b border-blue-100 flex items-center gap-3">
          <Facebook className="w-5 h-5 text-blue-600" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-zinc-900 text-sm truncate">{current.group.group_name}</p>
            {current.group.members > 0 && <p className="text-xs text-blue-600">{current.group.members.toLocaleString()} czlonkow</p>}
          </div>
        </div>

        {/* Content: image + text */}
        <div className="p-5">
          <div className="flex gap-4 mb-4">
            {current.imageUrl && (
              <div className="flex-shrink-0">
                <img src={current.imageUrl} alt="" className="w-28 h-28 rounded-xl object-cover border border-zinc-200" />
                <a href={current.imageUrl} download target="_blank" rel="noopener noreferrer" className="block text-center text-[10px] text-purple-600 hover:text-purple-700 mt-1 font-medium">
                  Pobierz
                </a>
              </div>
            )}
            <div className="flex-1 min-w-0 bg-zinc-50 rounded-xl p-3 border border-zinc-200">
              <p className="text-sm text-zinc-700 whitespace-pre-line">{current.postText}</p>
            </div>
          </div>

          {/* Action buttons */}
          {phase === 'ready' && (
            <button
              onClick={handlePublish}
              className="w-full py-4 rounded-xl bg-[#F47A3B] hover:bg-[#E06A2B] text-white font-bold text-base flex items-center justify-center gap-3 transition-colors shadow-lg shadow-[#F47A3B]/20"
              data-testid="publish-btn"
            >
              <Play className="w-5 h-5" /> PUBLIKUJ — kopiuje tekst + otwiera grupe
            </button>
          )}

          {phase === 'publishing' && (
            <div className="space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-sm font-bold text-amber-700 mb-2">Tekst skopiowany! Teraz:</p>
                <div className="space-y-2 text-xs text-amber-800">
                  <div className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">1</span>
                    <p>W grupie FB kliknij <strong>"Napisz cos..."</strong> (pole nowego posta)</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">2</span>
                    <p>Wklej tekst: <strong>Ctrl+V</strong> (Windows) lub <strong>Cmd+V</strong> (Mac)</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">3</span>
                    <p>Kliknij ikone <strong>zdjecia</strong> i dodaj grafike (pobierz nizej)</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-green-500 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">4</span>
                    <p>Kliknij <strong>"Opublikuj"</strong> na FB</p>
                  </div>
                </div>
                {current.imageUrl && (
                  <a href={current.imageUrl} download target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-xs bg-purple-500 hover:bg-purple-600 text-white px-3 py-1.5 rounded-lg transition-colors">
                    <ImageIcon className="w-3.5 h-3.5" /> Pobierz grafikę
                  </a>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={markDone} className="flex-1 py-3 rounded-xl bg-green-600 hover:bg-green-700 text-white font-bold flex items-center justify-center gap-2 transition-colors" data-testid="mark-done-btn">
                  <CheckCircle2 className="w-5 h-5" /> Gotowe — następna grupa
                </button>
                <button onClick={() => { setPhase('dm_flow'); }} className="py-3 px-4 rounded-xl bg-zinc-100 hover:bg-zinc-200 text-zinc-700 font-medium text-sm flex items-center gap-1.5 transition-colors">
                  <MessageSquare className="w-4 h-4" /> Flow DM
                </button>
              </div>
            </div>
          )}

          {phase === 'dm_flow' && current.responseFlow?.steps && (
            <div className="space-y-3">
              <div className="bg-zinc-50 rounded-xl p-4 space-y-2.5">
                <p className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Flow odpowiedzi (jak ktos napisze: {current.responseFlow.keywords?.slice(0,2).join(', ')})</p>
                {current.responseFlow.steps.map((step, si) => (
                  <div key={si} className="flex items-start gap-2.5">
                    <span className={`w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0 ${step.type === 'link' ? 'bg-[#F47A3B] text-white' : step.type === 'close' ? 'bg-green-500 text-white' : 'bg-zinc-300 text-white'}`}>{si+1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-medium text-zinc-400 mb-0.5">{step.label}</p>
                      <div className="bg-white rounded-lg p-2.5 border border-zinc-200 text-sm text-zinc-700 whitespace-pre-line">{step.text}</div>
                    </div>
                    <button onClick={() => {navigator.clipboard.writeText(step.text); toast.success('Skopiowano!');}} className="mt-6 text-zinc-400 hover:text-[#F47A3B] p-1">
                      <ClipboardCopy className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <button onClick={markDone} className="w-full py-3 rounded-xl bg-green-600 hover:bg-green-700 text-white font-bold flex items-center justify-center gap-2 transition-colors">
                <CheckCircle2 className="w-5 h-5" /> Gotowe — nastepna grupa
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   CAMPAIGN DETAIL VIEW
   ═══════════════════════════════════════════════════════════════════ */
function CampaignDetailView({ campaignId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [tab, setTab] = useState('queue'); // queue | plan | kits | posts
  const [weeklyPlan, setWeeklyPlan] = useState(null);
  const token = localStorage.getItem('token');

  const fetchCampaign = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/agency/campaigns/${campaignId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      toast.error('Blad ladowania kampanii');
    } finally {
      setLoading(false);
    }
  }, [campaignId, token]);

  useEffect(() => { fetchCampaign(); }, [fetchCampaign]);

  useEffect(() => {
    if (!data?.campaign) return;
    axios.get(`${API}/admin/agency/campaigns/${campaignId}/weekly-plan`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => setWeeklyPlan(res.data)).catch(() => {});
  }, [data, campaignId, token]);

  const handlePostAction = async (postId, action) => {
    setActionLoading(postId);
    try {
      await axios.post(`${API}/admin/booster/posts/${postId}/mark-${action}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Post oznaczony: ${action}`);
      fetchCampaign();
    } catch (err) {
      toast.error('Blad akcji');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCampaignAction = async (action) => {
    setActionLoading('campaign');
    try {
      await axios.post(`${API}/admin/agency/campaigns/${campaignId}/${action}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(action === 'start' ? 'Kampania uruchomiona' : action === 'pause' ? 'Kampania wstrzymana' : 'Kampania zakonczona');
      fetchCampaign();
    } catch (err) {
      toast.error('Blad akcji kampanii');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-6 h-6 animate-spin text-[#F47A3B]" />
    </div>
  );

  if (!data?.campaign) return (
    <div className="text-center py-16">
      <p className="text-zinc-500">Kampania nie znaleziona</p>
      <Button variant="outline" onClick={onBack} className="mt-3">Wstecz</Button>
    </div>
  );

  const { campaign, posts, groups } = data;
  const pendingPosts = posts.filter(p => ['PENDING', 'OPENED', 'COPIED'].includes(p.status));
  const donePosts = posts.filter(p => ['POSTED'].includes(p.status));
  const skippedPosts = posts.filter(p => ['SKIPPED', 'FAILED', 'REJECTED', 'BANNED'].includes(p.status));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-700 mb-2" data-testid="back-to-list">
            <ArrowLeft className="w-4 h-4" /> Wszystkie kampanie
          </button>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-zinc-900" data-testid="campaign-detail-title">{campaign.name}</h1>
            <StatusBadge status={campaign.status} />
          </div>
          {campaign.landing_url && (
            <a href={campaign.landing_url} target="_blank" rel="noopener noreferrer" className="text-sm text-[#F47A3B] hover:underline flex items-center gap-1 mt-1">
              <Globe className="w-3.5 h-3.5" /> {campaign.landing_url}
            </a>
          )}
        </div>
        <div className="flex gap-2">
          {campaign.status === 'DRAFT' && (
            <Button onClick={() => handleCampaignAction('start')} disabled={actionLoading === 'campaign'} className="bg-green-600 hover:bg-green-700 text-white gap-1.5" data-testid="detail-start">
              {actionLoading === 'campaign' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Uruchom
            </Button>
          )}
          {campaign.status === 'RUNNING' && (
            <>
              <Button onClick={() => handleCampaignAction('pause')} disabled={actionLoading === 'campaign'} variant="outline" className="gap-1.5 text-yellow-600" data-testid="detail-pause">
                <Pause className="w-4 h-4" /> Pauza
              </Button>
              <Button onClick={() => handleCampaignAction('finish')} disabled={actionLoading === 'campaign'} variant="outline" className="gap-1.5 text-blue-600" data-testid="detail-finish">
                <CheckCircle2 className="w-4 h-4" /> Zakoncz
              </Button>
            </>
          )}
          {campaign.status === 'PAUSED' && (
            <>
              <Button onClick={() => handleCampaignAction('start')} disabled={actionLoading === 'campaign'} className="bg-green-600 hover:bg-green-700 text-white gap-1.5" data-testid="detail-resume">
                <Play className="w-4 h-4" /> Wznow
              </Button>
              <Button onClick={() => handleCampaignAction('finish')} disabled={actionLoading === 'campaign'} variant="outline" className="gap-1.5 text-blue-600">
                <CheckCircle2 className="w-4 h-4" /> Zakoncz
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Stats overview */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-zinc-200 p-4 text-center">
          <p className="text-2xl font-bold text-zinc-900">{posts.length}</p>
          <p className="text-xs text-zinc-500">Wszystkie posty</p>
        </div>
        <div className="bg-green-50 rounded-xl border border-green-200 p-4 text-center">
          <p className="text-2xl font-bold text-green-700">{donePosts.length}</p>
          <p className="text-xs text-green-600">Opublikowane</p>
        </div>
        <div className="bg-blue-50 rounded-xl border border-blue-200 p-4 text-center">
          <p className="text-2xl font-bold text-blue-700">{pendingPosts.length}</p>
          <p className="text-xs text-blue-600">Do zrobienia</p>
        </div>
        <div className="bg-zinc-50 rounded-xl border border-zinc-200 p-4 text-center">
          <p className="text-2xl font-bold text-zinc-600">{skippedPosts.length}</p>
          <p className="text-xs text-zinc-500">Pominiete/blad</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-zinc-100 rounded-xl p-1">
        {[
          {id: 'queue', label: 'Kolejka', icon: Play},
          {id: 'plan', label: 'Plan tygodnia', icon: Calendar},
          {id: 'kits', label: 'Zestawy', icon: Target},
          {id: 'posts', label: 'Posty', icon: Send},
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-colors ${tab === t.id ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {/* KOLEJKA — tryb szybkiej publikacji */}
      {tab === 'queue' && weeklyPlan && (() => {
        // Build flat queue from weekly plan
        const allSlots = [];
        weeklyPlan.plan.forEach((day, di) => {
          day.groups?.forEach((g, gi) => {
            allSlots.push({
              dayNum: di,
              dayName: day.day_name,
              category: day.category,
              categoryLabel: day.category_label,
              postText: day.post_text,
              imageUrl: day.image_url,
              trackingUrl: day.tracking_url,
              cta: day.cta,
              responseFlow: day.response_flow,
              group: g,
              slotIdx: gi,
            });
          });
        });
        return <QueueView slots={allSlots} campaign={campaign} />;
      })()}

      {/* PLAN TYGODNIA */}
      {tab === 'plan' && weeklyPlan && (
        <div className="space-y-3">
          {weeklyPlan.plan.map((day, di) => {
            const CAT_COLORS = {
              'SPRZEDAZ': 'border-l-green-500 bg-green-50/30',
              'ELEKTRONIKA': 'border-l-blue-500 bg-blue-50/30',
              'RANDKI': 'border-l-pink-500 bg-pink-50/30',
              'MONETYZACJA': 'border-l-yellow-500 bg-yellow-50/30',
            };
            const CAT_BADGES = {
              'SPRZEDAZ': 'bg-green-100 text-green-700',
              'ELEKTRONIKA': 'bg-blue-100 text-blue-700',
              'RANDKI': 'bg-pink-100 text-pink-700',
              'MONETYZACJA': 'bg-yellow-100 text-yellow-700',
            };
            return (
              <div key={di} className={`bg-white rounded-xl border border-zinc-200 border-l-4 overflow-hidden ${CAT_COLORS[day.category] || 'border-l-zinc-300'}`}>
                {/* Day header */}
                <div className="px-4 py-3 flex items-center justify-between border-b border-zinc-100">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold text-zinc-900">Dzien {di + 1}</span>
                    <span className="text-xs text-zinc-500">{day.day_name}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${CAT_BADGES[day.category] || 'bg-zinc-100 text-zinc-600'}`}>{day.category_label}</span>
                  </div>
                  {day.tip && <span className="text-[10px] text-[#F47A3B] font-medium">{day.tip}</span>}
                </div>
                {/* Content: image + text + groups */}
                <div className="p-4">
                  <div className="flex gap-4 mb-3">
                    {day.image_url && (
                      <a href={day.image_url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                        <img src={day.image_url} alt="" className="w-20 h-20 rounded-lg object-cover border border-zinc-200" />
                      </a>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-zinc-700 whitespace-pre-line line-clamp-4">{day.post_text?.slice(0, 200)}</p>
                      <div className="flex gap-1.5 mt-2">
                        <button onClick={() => {navigator.clipboard.writeText(day.post_text); toast.success('Tekst skopiowany!');}} className="text-[10px] bg-zinc-100 hover:bg-zinc-200 text-zinc-600 px-2 py-0.5 rounded flex items-center gap-1">
                          <ClipboardCopy className="w-2.5 h-2.5" /> Kopiuj tekst
                        </button>
                        {day.tracking_url && (
                          <button onClick={() => {navigator.clipboard.writeText(day.tracking_url); toast.success('Link skopiowany!');}} className="text-[10px] bg-[#F47A3B]/10 text-[#F47A3B] px-2 py-0.5 rounded flex items-center gap-1">
                            <ExternalLink className="w-2.5 h-2.5" /> Link
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Groups for this day — quick publish mode */}
                  <div className="space-y-1.5">
                    <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">5 grup na dzis (kliknij = otwiera grupe + kopiuje tekst):</p>
                    {day.groups?.map((g, gi) => (
                      <div key={gi} className="flex items-center gap-2 text-xs">
                        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0 ${g.time_label === 'Rano' ? 'bg-amber-100 text-amber-600' : 'bg-indigo-100 text-indigo-600'}`}>{gi + 1}</span>
                        <span className="text-zinc-500 w-10 font-mono">{g.time}</span>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(day.post_text);
                            window.open(g.group_url, '_blank');
                            toast.success('Tekst skopiowany! Grupa otwarta — wklej i publikuj');
                          }}
                          className="flex-1 text-left bg-zinc-50 hover:bg-[#F47A3B]/5 border border-zinc-200 hover:border-[#F47A3B]/30 rounded-lg px-3 py-1.5 transition-all flex items-center gap-2 group"
                        >
                          <Play className="w-3 h-3 text-[#F47A3B] opacity-0 group-hover:opacity-100 transition-opacity" />
                          <span className="text-zinc-700 group-hover:text-[#F47A3B] truncate">{g.group_name}</span>
                          {g.members > 0 && <span className="text-zinc-400 text-[10px] ml-auto">{(g.members/1000).toFixed(0)}k</span>}
                        </button>
                      </div>
                    ))}
                    {day.image_url && (
                      <div className="flex items-center gap-2 mt-2 bg-purple-50 border border-purple-200 rounded-lg px-3 py-2">
                        <ImageIcon className="w-4 h-4 text-purple-500" />
                        <span className="text-xs text-purple-700 flex-1">Grafika do tego posta:</span>
                        <a href={day.image_url} download target="_blank" rel="noopener noreferrer" className="text-xs bg-purple-500 hover:bg-purple-600 text-white px-2.5 py-1 rounded-lg transition-colors">
                          Pobierz grafike
                        </a>
                      </div>
                    )}
                  </div>
                  {/* Response flow */}
                  {day.response_flow?.steps && (
                    <div className="mt-3 bg-zinc-50 rounded-lg p-2.5">
                      <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider mb-1.5">Flow odpowiedzi (keyword: {day.response_flow.keywords?.slice(0,2).join(', ')})</p>
                      {day.response_flow.steps.map((step, si) => (
                        <div key={si} className="flex items-start gap-2 mb-1 group">
                          <span className={`w-4 h-4 rounded-full text-[8px] font-bold flex items-center justify-center flex-shrink-0 ${step.type === 'link' ? 'bg-[#F47A3B] text-white' : 'bg-zinc-300 text-white'}`}>{si+1}</span>
                          <p className="text-[11px] text-zinc-600 flex-1">{step.text?.slice(0, 100)}</p>
                          <button onClick={() => {navigator.clipboard.writeText(step.text); toast.success('Skopiowano!');}} className="opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-zinc-600">
                            <ClipboardCopy className="w-2.5 h-2.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ZESTAWY POSTOW */}
      {tab === 'kits' && (
        <>
      {/* Post Kits — kompletne zestawy */}
      {campaign.post_kits?.length > 0 ? (
        <div className="space-y-4">
          <h3 className="font-semibold text-zinc-900 flex items-center gap-2">
            <Target className="w-4 h-4 text-[#F47A3B]" /> Zestawy postow ({campaign.post_kits.length})
          </h3>
          {campaign.post_kits.map((kit, i) => {
            const CAT_COLORS = {
              'SPRZEDAZ': 'bg-green-100 text-green-700 border-green-200',
              'ELEKTRONIKA': 'bg-blue-100 text-blue-700 border-blue-200',
              'RANDKI': 'bg-pink-100 text-pink-700 border-pink-200',
              'MONETYZACJA': 'bg-yellow-100 text-yellow-700 border-yellow-200',
            };
            const catClass = CAT_COLORS[kit.category] || 'bg-zinc-100 text-zinc-700 border-zinc-200';
            return (
              <div key={kit.id || i} className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
                {/* Kit header */}
                <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${catClass}`}>{kit.category_label || kit.category}</span>
                    <span className="text-xs text-zinc-500 font-mono">#{i + 1}</span>
                  </div>
                  {kit.cta && <span className="text-xs bg-[#F47A3B]/10 text-[#F47A3B] px-2 py-0.5 rounded-full font-medium">CTA: {kit.cta}</span>}
                </div>
                {/* Kit content — image + text side by side */}
                <div className="p-4 flex gap-4">
                  {kit.image_url && (
                    <a href={kit.image_url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                      <img src={kit.image_url} alt="" className="w-24 h-24 rounded-lg object-cover border border-zinc-200 hover:opacity-80 transition-opacity" />
                    </a>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-zinc-700 whitespace-pre-line line-clamp-6">{kit.text}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <button onClick={() => {navigator.clipboard.writeText(kit.text); toast.success('Tekst skopiowany!');}} className="text-xs bg-zinc-100 hover:bg-zinc-200 text-zinc-600 px-2.5 py-1 rounded-lg flex items-center gap-1 transition-colors">
                        <ClipboardCopy className="w-3 h-3" /> Kopiuj tekst
                      </button>
                      {kit.tracking_url && (
                        <button onClick={() => {navigator.clipboard.writeText(kit.tracking_url); toast.success('Link skopiowany!');}} className="text-xs bg-[#F47A3B]/10 hover:bg-[#F47A3B]/20 text-[#F47A3B] px-2.5 py-1 rounded-lg flex items-center gap-1 transition-colors">
                          <ExternalLink className="w-3 h-3" /> Kopiuj link
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                {/* Response flow */}
                {kit.response_flow && (
                  <div className="px-4 pb-4">
                    <div className="bg-zinc-50 rounded-lg p-3 space-y-2">
                      <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider flex items-center gap-1">
                        <MessageSquare className="w-3 h-3" /> Flow odpowiedzi
                        {kit.response_flow.keywords?.length > 0 && (
                          <span className="ml-1 text-[#F47A3B]">
                            (keyword: {kit.response_flow.keywords.slice(0, 3).join(', ')})
                          </span>
                        )}
                      </p>
                      {kit.response_flow.steps?.map((step, si) => (
                        <div key={si} className="flex items-start gap-2 group">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0 mt-0.5 ${
                            step.type === 'link' ? 'bg-[#F47A3B] text-white' : step.type === 'close' ? 'bg-green-500 text-white' : 'bg-zinc-300 text-white'
                          }`}>{si + 1}</div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[10px] font-medium text-zinc-500">{step.label}</p>
                            <p className="text-xs text-zinc-700 whitespace-pre-line">{step.text}</p>
                          </div>
                          <button onClick={() => {navigator.clipboard.writeText(step.text); toast.success('Skopiowano!');}} className="opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-zinc-600 p-0.5 transition-opacity">
                            <ClipboardCopy className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : campaign.copy_variants?.length > 0 && (
        <div className="bg-white rounded-xl border border-zinc-200 p-4">
          <h3 className="font-semibold text-zinc-900 mb-3 flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-[#F47A3B]" /> Warianty tresci ({campaign.copy_variants.length})
          </h3>
          <div className="space-y-2">
            {campaign.copy_variants.map((cv, i) => (
              <div key={i} className="bg-zinc-50 rounded-lg p-3 text-sm text-zinc-700">
                <span className="text-xs font-bold text-[#F47A3B] mr-2">Wariant {String.fromCharCode(65 + i)}</span>
                {cv.slice(0, 200)}{cv.length > 200 ? '...' : ''}
              </div>
            ))}
          </div>
        </div>
      )}
        </>
      )}

      {/* POSTY W GRUPACH */}
      {tab === 'posts' && (
      <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
          <h3 className="font-semibold text-zinc-900 flex items-center gap-2">
            <Send className="w-4 h-4 text-[#F47A3B]" /> Posty ({posts.length})
          </h3>
        </div>
        {posts.length === 0 ? (
          <div className="p-8 text-center text-zinc-500 text-sm">
            Uruchom kampanie, zeby wygenerowac posty
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {posts.map((post) => {
              const ps = POST_STATUS_MAP[post.status] || POST_STATUS_MAP.PENDING;
              return (
                <div key={post.id} className={`p-4 ${ps.bg} hover:bg-opacity-80 transition-colors`} data-testid={`post-${post.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-medium ${ps.color}`}>{ps.label}</span>
                        {post.variant_used && <span className="text-[10px] bg-zinc-200 text-zinc-600 px-1.5 py-0.5 rounded font-mono">VAR {post.variant_used}</span>}
                      </div>
                      <p className="text-sm font-medium text-zinc-900 truncate">
                        <Facebook className="w-3.5 h-3.5 inline mr-1 text-blue-500" />
                        {post.group_name || 'Grupa'}
                      </p>
                      {post.copy && <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{post.copy.slice(0, 120)}</p>}
                      {post.group_url && (
                        <a href={post.group_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline flex items-center gap-1 mt-1">
                          <ExternalLink className="w-3 h-3" /> Otworz grupe
                        </a>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {post.status === 'PENDING' && (
                        <>
                          <Button size="sm" variant="ghost" className="text-blue-600 h-8 px-2" onClick={() => handlePostAction(post.id, 'opened')} disabled={actionLoading === post.id} title="Otwarto grupe">
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-zinc-400 h-8 px-2" onClick={() => handlePostAction(post.id, 'skipped')} disabled={actionLoading === post.id} title="Pomin">
                            <SkipForward className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      )}
                      {post.status === 'OPENED' && (
                        <>
                          <Button size="sm" variant="ghost" className="text-indigo-600 h-8 px-2" onClick={() => handlePostAction(post.id, 'copied')} disabled={actionLoading === post.id} title="Skopiowano tresc">
                            <ClipboardCopy className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-green-600 h-8 px-2" onClick={() => handlePostAction(post.id, 'posted')} disabled={actionLoading === post.id} title="Opublikowano">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      )}
                      {post.status === 'COPIED' && (
                        <>
                          <Button size="sm" variant="ghost" className="text-green-600 h-8 px-2" onClick={() => handlePostAction(post.id, 'posted')} disabled={actionLoading === post.id} title="Opublikowano">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-500 h-8 px-2" onClick={() => handlePostAction(post.id, 'rejected')} disabled={actionLoading === post.id} title="Odrzucono">
                            <XCircle className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      )}
                      {post.status === 'POSTED' && post.post_url && (
                        <a href={post.post_url} target="_blank" rel="noopener noreferrer" className="text-green-600">
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                      {actionLoading === post.id && <Loader2 className="w-3.5 h-3.5 animate-spin text-zinc-400" />}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   NEW CAMPAIGN FORM
   ═══════════════════════════════════════════════════════════════════ */
function NewCampaignForm({ onBack, onCreated }) {
  const token = localStorage.getItem('token');
  const [saving, setSaving] = useState(false);
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [step, setStep] = useState('template'); // template | details
  const [form, setForm] = useState({
    name: '',
    objective: 'LEADS',
    landing_url: '',
    copy_variants: [''],
    comment1_variants: [''],
    group_ids: [],
    tags: [],
    posting_window_start: '08:30',
    posting_window_end: '21:30',
    max_posts_per_day: 5,
  });

  useEffect(() => {
    axios.get(`${API}/admin/booster/groups`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      const raw = res.data;
      setGroups(Array.isArray(raw) ? raw : (raw?.groups || []));
    }).catch(() => {}).finally(() => setLoadingGroups(false));
    
    axios.get(`${API}/admin/agency/templates`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => setTemplates(res.data?.templates || [])).catch(() => {});
  }, [token]);

  const applyTemplate = (tmpl) => {
    setSelectedTemplate(tmpl);
    setForm(f => ({
      ...f,
      name: '',
      landing_url: tmpl.product === 'CLOSI' ? 'https://closi.pl' : 'https://inpriv.pl',
      copy_variants: tmpl.copy_variants || [''],
      comment1_variants: tmpl.response_flow?.keywords || [''],
      tags: [tmpl.product.toLowerCase(), tmpl.id],
    }));
    setStep('details');
  };

  const updateField = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const addCopyVariant = () => updateField('copy_variants', [...form.copy_variants, '']);
  const removeCopyVariant = (i) => updateField('copy_variants', form.copy_variants.filter((_, idx) => idx !== i));
  const updateCopyVariant = (i, val) => {
    const cv = [...form.copy_variants];
    cv[i] = val;
    updateField('copy_variants', cv);
  };

  const toggleGroup = (gid) => {
    const ids = form.group_ids.includes(gid) ? form.group_ids.filter(id => id !== gid) : [...form.group_ids, gid];
    updateField('group_ids', ids);
  };

  const selectAllGroups = () => updateField('group_ids', groups.map(g => g.id));
  const deselectAllGroups = () => updateField('group_ids', []);

  const handleSubmit = async () => {
    if (!form.name.trim()) return toast.error('Podaj nazwe kampanii');
    if (!form.landing_url.trim()) return toast.error('Podaj URL strony docelowej');
    if (form.group_ids.length === 0) return toast.error('Wybierz przynajmniej jedna grupe');

    setSaving(true);
    try {
      const payload = {
        ...form,
        target_type: 'GENERIC',
        target_ids: [],
        utm_source: 'facebook',
        utm_medium: 'social',
        utm_campaign: form.name.toLowerCase().replace(/\s+/g, '-'),
        utm_content: '',
        asset_ids: [],
        min_break_minutes: 15,
        max_break_minutes: 45,
      };
      const res = await axios.post(`${API}/admin/agency/campaigns`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Kampania utworzona!');
      onCreated(res.data.id);
    } catch (err) {
      toast.error('Blad tworzenia kampanii');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-700 mb-2" data-testid="back-from-new">
          <ArrowLeft className="w-4 h-4" /> Wstecz
        </button>
        <h1 className="text-2xl font-bold text-zinc-900">{step === 'template' ? 'Wybierz typ kampanii' : 'Nowa kampania'}</h1>
      </div>

      {/* Step 1: Template selection */}
      {step === 'template' && (
        <div className="space-y-4">
          {['CLOSI', 'INPRIV'].map(product => {
            const productTemplates = templates.filter(t => t.product === product);
            if (productTemplates.length === 0) return null;
            return (
              <div key={product}>
                <h3 className="text-sm font-bold text-zinc-900 mb-2 flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${product === 'CLOSI' ? 'bg-green-500' : 'bg-blue-500'}`} />
                  {product === 'CLOSI' ? 'Closi — AI ogloszenia' : 'InPriv — Private chat'}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {productTemplates.map(tmpl => (
                    <button
                      key={tmpl.id}
                      onClick={() => applyTemplate(tmpl)}
                      className="text-left bg-white rounded-xl border border-zinc-200 p-4 hover:border-[#F47A3B] hover:shadow-md transition-all group"
                      data-testid={`template-${tmpl.id}`}
                    >
                      <p className="font-semibold text-zinc-900 text-sm group-hover:text-[#F47A3B] transition-colors">{tmpl.niche}</p>
                      <p className="text-xs text-zinc-500 mt-1">{tmpl.description}</p>
                      <p className="text-[10px] text-zinc-400 mt-2">{tmpl.copy_variants?.length || 0} wariantow • {tmpl.suggested_groups?.length || 0} sugestii grup</p>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
          <button onClick={() => { setSelectedTemplate(null); setStep('details'); }} className="text-sm text-zinc-500 hover:text-zinc-700 mt-2">
            Lub stworz kampanie od zera →
          </button>
        </div>
      )}

      {/* Step 2: Campaign details */}
      {step === 'details' && (
        <>
      {selectedTemplate && (
        <div className="bg-[#F47A3B]/5 border border-[#F47A3B]/20 rounded-xl p-3 flex items-center gap-3">
          <Target className="w-5 h-5 text-[#F47A3B]" />
          <div>
            <p className="text-sm font-medium text-zinc-900">Szablon: {selectedTemplate.product} / {selectedTemplate.niche}</p>
            <p className="text-xs text-zinc-500">{selectedTemplate.copy_variants?.length} wariantow tekstow zaladowanych</p>
          </div>
          <button onClick={() => setStep('template')} className="ml-auto text-xs text-zinc-500 hover:text-zinc-700">Zmien</button>
        </div>
      )}

      {/* Name */}
      <div className="bg-white rounded-xl border border-zinc-200 p-4 space-y-4">
        <div>
          <label className="text-sm font-medium text-zinc-700 mb-1 block">Nazwa kampanii (klient + nisza)</label>
          <input
            type="text"
            placeholder={selectedTemplate ? `np. Jan Kowalski — ${selectedTemplate.niche}` : "np. Iwona Cholewa — Golden Retriever"}
            value={form.name}
            onChange={e => updateField('name', e.target.value)}
            className="w-full px-4 py-2.5 rounded-lg border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#F47A3B]/30 focus:border-[#F47A3B]"
            data-testid="campaign-name-input"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-zinc-700 mb-1 block">URL strony docelowej</label>
          <input
            type="url"
            placeholder="https://apisai.pl/hodowla/..."
            value={form.landing_url}
            onChange={e => updateField('landing_url', e.target.value)}
            className="w-full px-4 py-2.5 rounded-lg border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#F47A3B]/30 focus:border-[#F47A3B]"
            data-testid="campaign-url-input"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-zinc-700 mb-1 block">Cel</label>
          <div className="flex gap-2">
            {[{v:'LEADS', l:'Leady'}, {v:'TRAFFIC', l:'Ruch'}, {v:'BRAND', l:'Marka'}].map(o => (
              <button
                key={o.v}
                onClick={() => updateField('objective', o.v)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  form.objective === o.v ? 'bg-[#F47A3B] text-white' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                }`}
              >
                {o.l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Copy variants */}
      <div className="bg-white rounded-xl border border-zinc-200 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-zinc-900 text-sm">Warianty tresci (A/B)</h3>
          <Button size="sm" variant="outline" onClick={addCopyVariant} className="gap-1 text-xs">
            <Plus className="w-3 h-3" /> Dodaj wariant
          </Button>
        </div>
        {form.copy_variants.map((cv, i) => (
          <div key={i} className="relative">
            <span className="absolute top-2.5 left-3 text-xs font-bold text-[#F47A3B]">{String.fromCharCode(65 + i)}</span>
            <textarea
              value={cv}
              onChange={e => updateCopyVariant(i, e.target.value)}
              placeholder="Tresc posta na FB..."
              rows={4}
              className="w-full pl-8 pr-10 py-2.5 rounded-lg border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#F47A3B]/30 focus:border-[#F47A3B] resize-none"
              data-testid={`copy-variant-${i}`}
            />
            {form.copy_variants.length > 1 && (
              <button onClick={() => removeCopyVariant(i)} className="absolute top-2.5 right-2.5 text-zinc-400 hover:text-red-500">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Groups selection */}
      <div className="bg-white rounded-xl border border-zinc-200 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-zinc-900 text-sm">
            Grupy FB ({form.group_ids.length} wybranych)
          </h3>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={selectAllGroups} className="text-xs text-[#F47A3B]">Zaznacz wszystkie</Button>
            <Button size="sm" variant="ghost" onClick={deselectAllGroups} className="text-xs text-zinc-500">Odznacz</Button>
          </div>
        </div>
        {loadingGroups ? (
          <Loader2 className="w-5 h-5 animate-spin text-zinc-400 mx-auto" />
        ) : groups.length === 0 ? (
          <p className="text-sm text-zinc-500 text-center py-4">Brak grup. Dodaj grupy w ustawieniach.</p>
        ) : (
          <div className="max-h-64 overflow-y-auto space-y-1">
            {groups.map(g => (
              <label key={g.id} className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${
                form.group_ids.includes(g.id) ? 'bg-[#F47A3B]/5 border border-[#F47A3B]/20' : 'hover:bg-zinc-50 border border-transparent'
              }`}>
                <input
                  type="checkbox"
                  checked={form.group_ids.includes(g.id)}
                  onChange={() => toggleGroup(g.id)}
                  className="rounded border-zinc-300 text-[#F47A3B] focus:ring-[#F47A3B]"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-zinc-900 truncate">{g.name}</p>
                  <p className="text-xs text-zinc-500">{g.members ? `${g.members.toLocaleString()} czl.` : ''} {g.category}</p>
                </div>
                {g.status !== 'ACTIVE' && (
                  <span className="text-[10px] bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded">{g.status}</span>
                )}
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Schedule */}
      <div className="bg-white rounded-xl border border-zinc-200 p-4 space-y-3">
        <h3 className="font-semibold text-zinc-900 text-sm flex items-center gap-2"><Calendar className="w-4 h-4 text-[#F47A3B]" /> Harmonogram</h3>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-zinc-500 mb-1 block">Od godz.</label>
            <input type="time" value={form.posting_window_start} onChange={e => updateField('posting_window_start', e.target.value)} className="w-full px-3 py-2 rounded-lg border border-zinc-200 text-sm" />
          </div>
          <div>
            <label className="text-xs text-zinc-500 mb-1 block">Do godz.</label>
            <input type="time" value={form.posting_window_end} onChange={e => updateField('posting_window_end', e.target.value)} className="w-full px-3 py-2 rounded-lg border border-zinc-200 text-sm" />
          </div>
          <div>
            <label className="text-xs text-zinc-500 mb-1 block">Max postow/dzien</label>
            <input type="number" value={form.max_posts_per_day} onChange={e => updateField('max_posts_per_day', parseInt(e.target.value) || 1)} min={1} max={50} className="w-full px-3 py-2 rounded-lg border border-zinc-200 text-sm" />
          </div>
        </div>
      </div>

      {/* Submit */}
      <Button onClick={handleSubmit} disabled={saving} className="w-full bg-[#F47A3B] hover:bg-[#E06A2B] text-white py-3 gap-2" data-testid="create-campaign-submit">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Megaphone className="w-4 h-4" />}
        Stworz kampanie
      </Button>
        </>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   GROUPS MANAGER
   ═══════════════════════════════════════════════════════════════════ */
function GroupsManager({ onBack }) {
  const token = localStorage.getItem('token');
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: '', url: '', category: 'SPRZEDAZ', members: 0 });
  const [saving, setSaving] = useState(false);

  const fetchGroups = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/booster/groups`, { headers: { Authorization: `Bearer ${token}` } });
      const raw = res.data;
      setGroups(Array.isArray(raw) ? raw : (raw?.groups || []));
    } catch (err) {
      toast.error('Blad ladowania grup');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const handleAdd = async () => {
    if (!newGroup.name.trim() || !newGroup.url.trim()) return toast.error('Podaj nazwe i URL grupy');
    setSaving(true);
    try {
      const url = `${API}/admin/booster/groups?name=${encodeURIComponent(newGroup.name)}&url=${encodeURIComponent(newGroup.url)}&category=${newGroup.category}&members=${newGroup.members}`;
      await axios.post(url, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Grupa dodana!');
      setNewGroup({ name: '', url: '', category: 'SPRZEDAZ', members: 0 });
      setShowAdd(false);
      fetchGroups();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Blad dodawania');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (groupId) => {
    if (!window.confirm('Usunac grupe?')) return;
    try {
      await axios.delete(`${API}/admin/booster/groups/${groupId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Grupa usunieta');
      fetchGroups();
    } catch (err) {
      toast.error('Blad usuwania');
    }
  };

  const filtered = groups.filter(g => {
    if (!search) return true;
    const s = search.toLowerCase();
    return g.name?.toLowerCase().includes(s) || g.url?.toLowerCase().includes(s) || g.tags?.some(t => t.includes(s));
  });

  const CATEGORIES = {
    'SPRZEDAZ': 'Sprzedaz',
    'SPRZEDAZ_INPRIV': 'Sprzedaz (InPriv)',
    'ELEKTRONIKA_INPRIV': 'Elektronika',
    'RANDKI_INPRIV': 'Randki',
    'ZAROBKI_INPRIV': 'Zarobki',
    'BIZNES': 'Biznes',
    'HODOWCY_OGOLNE': 'Hodowcy',
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-700 mb-2" data-testid="back-from-groups">
            <ArrowLeft className="w-4 h-4" /> Kampanie
          </button>
          <h1 className="text-2xl font-bold text-zinc-900" data-testid="groups-title">Grupy FB ({groups.length})</h1>
        </div>
        <Button onClick={() => setShowAdd(!showAdd)} className="bg-[#F47A3B] hover:bg-[#E06A2B] text-white gap-2" data-testid="add-group-btn">
          <Plus className="w-4 h-4" /> Dodaj grupe
        </Button>
      </div>

      {/* Add new group form */}
      {showAdd && (
        <div className="bg-white rounded-xl border border-zinc-200 p-4 space-y-3">
          <h3 className="font-semibold text-zinc-900 text-sm">Nowa grupa</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input type="text" placeholder="Nazwa grupy" value={newGroup.name} onChange={e => setNewGroup(g => ({...g, name: e.target.value}))} className="px-3 py-2 rounded-lg border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#F47A3B]/30" data-testid="group-name-input" />
            <input type="url" placeholder="https://facebook.com/groups/..." value={newGroup.url} onChange={e => setNewGroup(g => ({...g, url: e.target.value}))} className="px-3 py-2 rounded-lg border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#F47A3B]/30" data-testid="group-url-input" />
            <select value={newGroup.category} onChange={e => setNewGroup(g => ({...g, category: e.target.value}))} className="px-3 py-2 rounded-lg border border-zinc-200 text-sm">
              {Object.entries(CATEGORIES).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <input type="number" placeholder="Czlonkowie" value={newGroup.members || ''} onChange={e => setNewGroup(g => ({...g, members: parseInt(e.target.value) || 0}))} className="px-3 py-2 rounded-lg border border-zinc-200 text-sm" />
          </div>
          <div className="flex gap-2">
            <Button onClick={handleAdd} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white gap-1.5 text-sm" data-testid="save-group-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Dodaj
            </Button>
            <Button variant="outline" onClick={() => setShowAdd(false)} className="text-sm">Anuluj</Button>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
        <input type="text" placeholder="Szukaj grupy..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#F47A3B]/30" />
      </div>

      {/* Groups list */}
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#F47A3B]" /></div>
      ) : filtered.length === 0 ? (
        <p className="text-center text-zinc-500 py-10">Brak grup</p>
      ) : (
        <div className="space-y-2">
          {filtered.map(g => (
            <div key={g.id} className="bg-white rounded-xl border border-zinc-200 p-3 flex items-center gap-3 hover:shadow-sm">
              <Facebook className="w-5 h-5 text-blue-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-zinc-900 truncate">{g.name}</p>
                <div className="flex items-center gap-2 text-xs text-zinc-500">
                  {g.url && <a href={g.url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline truncate max-w-[200px]">{g.url.replace('https://www.facebook.com/groups/','').replace('https://facebook.com/groups/','')}</a>}
                  {g.members > 0 && <span>{g.members.toLocaleString()} czl.</span>}
                  <span className="bg-zinc-100 px-1.5 py-0.5 rounded text-[10px]">{CATEGORIES[g.category] || g.category}</span>
                </div>
              </div>
              <button onClick={() => handleDelete(g.id)} className="text-zinc-300 hover:text-red-500 p-1" title="Usun">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   MAIN AGENCY PANEL
   ═══════════════════════════════════════════════════════════════════ */
export default function AgencyPanel() {
  const [view, setView] = useState('list'); // list | detail | new | groups
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [globalStats, setGlobalStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/agency/campaigns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const raw = res.data;
      const list = Array.isArray(raw) ? raw : (raw?.campaigns || []);
      
      // Auto-cleanup fake groups (runs once, idempotent)
      try {
        await axios.post(`${API}/admin/agency/cleanup-fake-groups`, {}, { headers: { Authorization: `Bearer ${token}` } });
      } catch (e) {}

      // Auto-seed Closi + InPriv if they don't exist yet
      const hasClosi = list.some(c => c.tags?.includes('closi'));
      const hasInpriv = list.some(c => c.tags?.includes('inpriv'));
      if (!hasClosi || !hasInpriv) {
        try {
          let seeded = false;
          if (!hasClosi) {
            try {
              const r = await axios.post(`${API}/admin/agency/seed-closi`, {}, { headers: { Authorization: `Bearer ${token}` } });
              if (r.data?.status === 'seeded') seeded = true;
            } catch (e) {}
          }
          if (!hasInpriv) {
            try {
              const r = await axios.post(`${API}/admin/agency/seed-inpriv`, {}, { headers: { Authorization: `Bearer ${token}` } });
              if (r.data?.status === 'seeded') seeded = true;
            } catch (e) {}
          }
          if (seeded) {
            toast.success('Nowe kampanie dodane!');
            const res2 = await axios.get(`${API}/admin/agency/campaigns`, { headers: { Authorization: `Bearer ${token}` } });
            const raw2 = res2.data;
            setCampaigns(Array.isArray(raw2) ? raw2 : (raw2?.campaigns || []));
            setLoading(false);
            return;
          }
        } catch (seedErr) {}
      }
      
      setCampaigns(list);
      if (raw?.global_stats) setGlobalStats(raw.global_stats);
    } catch (err) {
      toast.error('Blad ladowania kampanii');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  const handleAction = async (campaignId, action) => {
    try {
      await axios.post(`${API}/admin/agency/campaigns/${campaignId}/${action}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(
        action === 'start' ? 'Kampania uruchomiona!' :
        action === 'pause' ? 'Kampania wstrzymana' :
        action === 'finish' ? 'Kampania zakonczona' :
        action === 'duplicate' ? 'Kampania zduplikowana' : 'OK'
      );
      fetchCampaigns();
    } catch (err) {
      toast.error('Blad');
    }
  };

  return (
    <PanelLayout>
      <div className="p-4 sm:p-6 max-w-5xl mx-auto" data-testid="agency-panel">
        {view === 'list' && (
          <CampaignListView
            campaigns={campaigns}
            loading={loading}
            globalStats={globalStats}
            onSelect={id => { setSelectedCampaignId(id); setView('detail'); }}
            onNew={() => setView('new')}
            onGroups={() => setView('groups')}
            onRefresh={fetchCampaigns}
            onAction={handleAction}
          />
        )}
        {view === 'detail' && selectedCampaignId && (
          <CampaignDetailView
            campaignId={selectedCampaignId}
            onBack={() => { setView('list'); fetchCampaigns(); }}
          />
        )}
        {view === 'new' && (
          <NewCampaignForm
            onBack={() => setView('list')}
            onCreated={(id) => { setSelectedCampaignId(id); setView('detail'); fetchCampaigns(); }}
          />
        )}
        {view === 'groups' && (
          <GroupsManager onBack={() => setView('list')} />
        )}
      </div>
    </PanelLayout>
  );
}
